import { API_BASE_URL } from '@/api/config'
import { useTenantStore } from '@/stores/tenantStore'

/**
 * Minimal authed JSON fetch for the gravitutor proxy endpoints.
 *
 * TODO(genapi): once `yarn genapi` adds `/gravitutor/*` to the generated
 * OpenAPI types, delete this helper and route the hooks through the typed
 * `$api`/`fetchClient` (`@/api/typedClient`) instead — that client already
 * injects the bearer token + `X-Tenant-Name` header via middleware. Until the
 * paths exist in `paths`, the typed client rejects them at compile time, so we
 * mirror its header injection here.
 */

const getToken = () => localStorage.getItem('accessToken')

const buildUrl = (path: string): string => {
  const base = API_BASE_URL.endsWith('/')
    ? API_BASE_URL.slice(0, -1)
    : API_BASE_URL
  const cleanPath = path.startsWith('/') ? path : `/${path}`
  return `${base}${cleanPath}`
}

const authedHeaders = (extra?: HeadersInit): Headers => {
  const headers = new Headers(extra)
  const token = getToken()
  if (token) headers.set('Authorization', `Bearer ${token}`)
  // Mirror typedClient: only set tenant if the caller didn't already.
  if (!headers.has('X-Tenant-Name')) {
    const tenant = useTenantStore.getState().selectedTenant?.name
    if (tenant) headers.set('X-Tenant-Name', tenant)
  }
  return headers
}

export class GraviRequestError extends Error {
  status: number
  detail: unknown
  constructor(status: number, detail: unknown, message: string) {
    super(message)
    this.name = 'GraviRequestError'
    this.status = status
    this.detail = detail
  }
}

async function parseError(response: Response): Promise<never> {
  const body = await response.json().catch(() => null)
  const detail = (body as { detail?: unknown } | null)?.detail
  const message =
    typeof detail === 'string'
      ? detail
      : `Request failed with status ${response.status}`
  throw new GraviRequestError(response.status, detail, message)
}

export async function graviGet<T>(
  path: string,
  signal?: AbortSignal,
): Promise<T> {
  const response = await fetch(buildUrl(path), {
    method: 'GET',
    headers: authedHeaders(),
    signal,
  })
  if (!response.ok) await parseError(response)
  return response.json() as Promise<T>
}

export async function graviSend<T>(
  method: 'POST' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const response = await fetch(buildUrl(path), {
    method,
    headers: authedHeaders(
      body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    ),
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  })
  if (!response.ok) await parseError(response)
  // DELETE may return 204/empty; tolerate that.
  if (response.status === 204) return undefined as T
  const text = await response.text()
  return (text ? JSON.parse(text) : undefined) as T
}
